/**
 * Utility functions for CSV export
 */

/**
 * Split a school name into first name and last name parts
 * @param {string} schoolName - Full school name
 * @returns {Object} - Object with firstName and lastName properties
 */
export const splitSchoolName = (schoolName) => {
  if (!schoolName) return { firstName: '', lastName: '' };
  
  const schoolTypes = [
    'State High School',
    'State School',
    'Primary School',
    'Secondary College',
    'College',
    'Grammar School',
    'Catholic College',
    'Catholic School',
    'Christian College',
    'Christian School',
    'Independent School',
    'High School',
    'School',
    'Academy'
  ];
  
  // Try to find a school type in the name
  for (const type of schoolTypes) {
    if (schoolName.includes(type)) {
      const index = schoolName.indexOf(type);
      return {
        firstName: schoolName.substring(0, index).trim(),
        lastName: schoolName.substring(index).trim()
      };
    }
  }
  
  // If no school type found, split at the last space
  const lastSpaceIndex = schoolName.lastIndexOf(' ');
  if (lastSpaceIndex > 0) {
    return {
      firstName: schoolName.substring(0, lastSpaceIndex).trim(),
      lastName: schoolName.substring(lastSpaceIndex + 1).trim()
    };
  }
  
  // If no space found, use the whole name as the first name
  return {
    firstName: schoolName,
    lastName: ''
  };
};

/**
 * Generate a CSV file for schools
 * @param {Array} organizations - List of school organizations
 * @returns {string} - CSV content
 */
export const generateSchoolsCsv = (organizations) => {
  // CSV header from the template
  const header = 'Customer ID,Organisation Name,ABN (Aust. Business Number),Website,Street Address Line 1 (street),Street Address Line 2 (Suburb),Street Address Line 3 (State),Street Address Line 4 (Postcode),Street Address Line 5 (Country),Billing Address Line 1 (PO Box),Billing Address Line 2 (Suburb),Billing Address Line 3 (State),Billing Address Line 4 (Postcode),Billing Address Line 5 (Country),1st Contact - First Name,1st Contact - Last Name,1st Contact - Position,1st Contact - Email Address,1st Contact - Phone,2nd Contact - First Name,2nd Contact - Last Name,2nd Contact - Position,2nd Contact - Email Address,2nd Contact - Phone,3rd Contact - First Name,3rd Contact - Last Name,3rd Contact - Position,3rd Contact - Email Address,3rd Contact - Phone';
  
  // Generate rows for each organization
  const rows = organizations.map(org => {
    const { firstName, lastName } = splitSchoolName(org.name || '');
    
    // Format all fields according to the requirements
    return [
      org.id || '',                      // Customer ID
      org.name || '',                    // Organisation Name
      '',                                // ABN (empty)
      org.website || '',                 // Website
      org.address || '',                 // Street Address Line 1
      org.suburb || '',                  // Street Address Line 2 (Suburb)
      org.state || '',                   // Street Address Line 3 (State)
      org.postcode || '',                // Street Address Line 4 (Postcode)
      'Australia',                       // Street Address Line 5 (Country)
      '',                                // Billing Address Line 1 (empty)
      '',                                // Billing Address Line 2 (empty)
      '',                                // Billing Address Line 3 (empty)
      '',                                // Billing Address Line 4 (empty)
      '',                                // Billing Address Line 5 (empty)
      firstName,                         // 1st Contact - First Name
      lastName,                          // 1st Contact - Last Name
      '',                                // 1st Contact - Position (empty)
      org.generalEmail || '',            // 1st Contact - Email Address
      org.generalPhone || '',            // 1st Contact - Phone
      '',                                // 2nd Contact - First Name (empty)
      '',                                // 2nd Contact - Last Name (empty)
      '',                                // 2nd Contact - Position (empty)
      '',                                // 2nd Contact - Email Address (empty)
      '',                                // 2nd Contact - Phone (empty)
      '',                                // 3rd Contact - First Name (empty)
      '',                                // 3rd Contact - Last Name (empty)
      '',                                // 3rd Contact - Position (empty)
      '',                                // 3rd Contact - Email Address (empty)
      ''                                 // 3rd Contact - Phone (empty)
    ].map(field => {
      // Convert field to string to ensure includes method is available
      const fieldStr = field === null || field === undefined ? '' : String(field);
      
      // Escape quotes and wrap fields with commas in quotes
      if (fieldStr && (fieldStr.includes(',') || fieldStr.includes('"'))) {
        return `"${fieldStr.replace(/"/g, '""')}"`;
      }
      return fieldStr;
    }).join(',');
  });
  
  // Combine header and rows
  return [header, ...rows].join('\n');
};

/**
 * Download a CSV file
 * @param {string} content - CSV content
 * @param {string} filename - Name of the file to download
 */
export const downloadCsv = (content, filename = 'schools.csv') => {
  // Create a blob with the CSV content
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  
  // Create a download link
  const link = document.createElement('a');
  
  // Check if browser supports download attribute
  if (navigator.msSaveBlob) {
    // For IE and Edge
    navigator.msSaveBlob(blob, filename);
  } else {
    // For other browsers
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}; 